import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, Auth } from "aws-amplify";
import Header from "../components/header/Header";

const BATCH_SIZE = 200;

const AdminAddContext = () => {
  const navigate = useNavigate();
  
  // Tab State
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Loading States
  const [loading, setLoading] = useState(false);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Analytics
  const [analytics, setAnalytics] = useState({
    totalCandidates: 0,
    totalInterviews: 0,
    completedEvaluations: 0,
    pending: 0,
  });
  
  // Candidates State
  const [allCandidates, setAllCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [paginationToken, setPaginationToken] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateEvaluations, setCandidateEvaluations] = useState([]);
  
  // Interview Slots State
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [interviewSlots, setInterviewSlots] = useState([]);
  
  // Messages
  const [message, setMessage] = useState({ type: "", text: "" });

  // Initialize
  useEffect(() => {
    checkAuthAndFetch();
  }, []);

  const checkAuthAndFetch = async () => {
    try {
      await Auth.currentAuthenticatedUser();
      fetchAnalytics();
      fetchCandidates();
    } catch (error) {
      setMessage({ type: "error", text: "Please sign in to access admin features." });
      setLoadingAnalytics(false);
    }
  };

  // Fetch Analytics
  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const [candidates, interviews, evaluations] = await Promise.all([
        fetchCount(`query { listCandidateProfiles(limit: 1000) { items { id } nextToken } }`, 'listCandidateProfiles'),
        fetchCount(`query { listInterviewSlots(limit: 1000) { items { id } nextToken } }`, 'listInterviewSlots'),
        fetchCount(`query { listInterviewEvaluations(limit: 1000) { items { id } nextToken } }`, 'listInterviewEvaluations'),
      ]);
      
      setAnalytics({
        totalCandidates: candidates.count,
        totalInterviews: interviews.count,
        completedEvaluations: evaluations.count,
        pending: Math.max(0, interviews.count - evaluations.count),
        candidatesHasMore: candidates.hasMore,
        interviewsHasMore: interviews.hasMore,
        evaluationsHasMore: evaluations.hasMore,
      });
    } catch (e) {
      console.error("Analytics error:", e);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const fetchCount = async (query, key) => {
    try {
      let response;
      try {
        response = await API.graphql({ query });
      } catch (e) {
        if (e.data?.[key]) response = e;
        else return { count: 0, hasMore: false };
      }
      const data = response?.data?.[key];
      const count = data?.items?.filter(i => i)?.length || 0;
      const hasMore = !!data?.nextToken;
      return { count, hasMore };
    } catch {
      return { count: 0, hasMore: false };
    }
  };

  // Fetch Candidates with Pagination
  const fetchCandidates = async (loadMore = false) => {
    if (loadMore) setIsLoadingMore(true);
    else {
      setIsLoadingCandidates(true);
      setAllCandidates([]);
      setPaginationToken(null);
    }
    
    try {
      let response;
      try {
        response = await API.graphql({
          query: `query ListCandidateProfiles($limit: Int, $nextToken: String) {
            listCandidateProfiles(limit: $limit, nextToken: $nextToken) {
              items {
                id fullName email mobileNumber photoURL currentCity roleApplyFor
                workStatus description keySkills educationDetails preferredSalary
                workPreference workMode matchedPercentage resumeURL createdAt
              }
              nextToken
            }
          }`,
          variables: { limit: BATCH_SIZE, nextToken: loadMore ? paginationToken : null }
        });
      } catch (e) {
        if (e.data?.listCandidateProfiles) response = e;
        else throw e;
      }

      if (response?.data?.listCandidateProfiles) {
        const items = response.data.listCandidateProfiles.items?.filter(i => i?.email) || [];
        const newToken = response.data.listCandidateProfiles.nextToken;
        
        setPaginationToken(newToken);
        setHasMore(!!newToken);
        
        const updated = loadMore ? [...allCandidates, ...items] : items;
        updated.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setAllCandidates(updated);
        if (!searchQuery.trim()) setFilteredCandidates(updated);
      }
    } catch (e) {
      console.error("Fetch candidates error:", e);
      setMessage({ type: "error", text: "Failed to load candidates." });
    } finally {
      setIsLoadingCandidates(false);
      setIsLoadingMore(false);
    }
  };

  // Search with debounce and server-side fallback
  useEffect(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      setFilteredCandidates(allCandidates);
      return;
    }
    
    // Local search first
    const local = allCandidates.filter(c =>
      c.fullName?.toLowerCase().includes(query) ||
      c.email?.toLowerCase().includes(query) ||
      c.mobileNumber?.includes(query) ||
      c.roleApplyFor?.toLowerCase().includes(query)
    );
    setFilteredCandidates(local);
    
    // Server search if local results insufficient
    if (local.length < 5 && hasMore) {
      const timer = setTimeout(() => searchServerSide(query), 500);
      return () => clearTimeout(timer);
    }
  }, [searchQuery, allCandidates, hasMore]);

  const searchServerSide = async (query) => {
    setIsSearching(true);
    try {
      let nextToken = null, found = [];
      const filter = {
        or: [
          { email: { contains: query } },
          { fullName: { contains: query } },
          { mobileNumber: { contains: query } }
        ]
      };

      do {
        let response;
        try {
          response = await API.graphql({
            query: `query ListCandidateProfiles($filter: ModelCandidateProfileFilterInput, $limit: Int, $nextToken: String) {
              listCandidateProfiles(filter: $filter, limit: $limit, nextToken: $nextToken) {
                items {
                  id fullName email mobileNumber photoURL currentCity roleApplyFor
                  workStatus description keySkills educationDetails preferredSalary
                  workPreference workMode matchedPercentage resumeURL createdAt
                }
                nextToken
              }
            }`,
            variables: { filter, limit: BATCH_SIZE, nextToken }
          });
        } catch (e) {
          if (e.data?.listCandidateProfiles) response = e;
          else break;
        }
        
        if (response?.data?.listCandidateProfiles) {
          found = [...found, ...response.data.listCandidateProfiles.items.filter(i => i?.email)];
          nextToken = response.data.listCandidateProfiles.nextToken;
        } else break;
        
        if (found.length >= 50) break;
      } while (nextToken);

      if (found.length > 0) {
        found.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setFilteredCandidates(found);
      }
    } catch (e) {
      console.error("Server search error:", e);
    } finally {
      setIsSearching(false);
    }
  };

  // View Candidate Details with Evaluations
  const viewCandidateDetails = async (candidate) => {
    setSelectedCandidate(candidate);
    setCandidateEvaluations([]);
    
    try {
      let response;
      try {
        response = await API.graphql({
          query: `query ListInterviewEvaluations($filter: ModelInterviewEvaluationFilterInput, $limit: Int) {
            listInterviewEvaluations(filter: $filter, limit: $limit) {
              items {
                id candidateEmail roundNumber evaluation feedbackForAI
                transcript videoURL interviewSlotID createdAt
              }
            }
          }`,
          variables: { filter: { candidateEmail: { eq: candidate.email } }, limit: 100 }
        });
      } catch (e) {
        if (e.data?.listInterviewEvaluations) response = e;
      }
      
      if (response?.data?.listInterviewEvaluations) {
        setCandidateEvaluations(response.data.listInterviewEvaluations.items?.filter(i => i?.id) || []);
      }
    } catch (e) {
      console.error("Fetch evaluations error:", e);
    }
  };

  // Fetch Interview Slots by Date Range
  const fetchSlotsByDate = async () => {
    if (!startDate) return;
    setLoading(true);
    setInterviewSlots([]);
    
    try {
      let nextToken = null, slots = [];
      
      // Build filter based on whether we have a range or single date
      let dateFilter;
      if (endDate && endDate !== startDate) {
        // Date range query using between
        dateFilter = { date: { between: [startDate, endDate] } };
      } else {
        // Single date query
        dateFilter = { date: { eq: startDate } };
      }
      
      do {
        let response;
        try {
          response = await API.graphql({
            query: `query ListInterviewSlots($filter: ModelInterviewSlotFilterInput, $limit: Int, $nextToken: String) {
              listInterviewSlots(filter: $filter, limit: $limit, nextToken: $nextToken) {
                items { id date startTime endTime candidateEmail meetingLinkURL status createdAt }
                nextToken
              }
            }`,
            variables: { filter: dateFilter, limit: 100, nextToken }
          });
        } catch (e) {
          if (e.data?.listInterviewSlots) response = e;
          else break;
        }
        
        if (response?.data?.listInterviewSlots) {
          slots = [...slots, ...response.data.listInterviewSlots.items.filter(i => i?.id)];
          nextToken = response.data.listInterviewSlots.nextToken;
        } else break;
      } while (nextToken);

      // Sort by date first, then by start time
      slots.sort((a, b) => {
        const dateCompare = (a.date || '').localeCompare(b.date || '');
        if (dateCompare !== 0) return dateCompare;
        return (a.startTime || '').localeCompare(b.startTime || '');
      });
      setInterviewSlots(slots);
    } catch (e) {
      console.error("Fetch slots error:", e);
      setMessage({ type: "error", text: "Failed to fetch interview slots." });
    } finally {
      setLoading(false);
    }
  };

  // Delete Slot
  const deleteSlot = async (slotId) => {
    try {
      await API.graphql({
        query: `mutation DeleteInterviewSlot($input: DeleteInterviewSlotInput!) {
          deleteInterviewSlot(input: $input) { id }
        }`,
        variables: { input: { id: slotId } }
      });
      
      setInterviewSlots(s => s.filter(x => x.id !== slotId));
      
      setMessage({ type: "success", text: "Slot deleted." });
      fetchAnalytics();
    } catch (e) {
      setMessage({ type: "error", text: "Failed to delete slot." });
    }
  };

  // Open Profile Page - Uses admin route with query params
  const openProfilePage = (email, candidate = null, slotId = null) => {
    const params = new URLSearchParams();
    params.set('email', email);
    
    // Add candidate details if available
    if (candidate) {
      if (candidate.fullName) params.set('fullName', candidate.fullName);
      if (candidate.photoURL) params.set('photoURL', candidate.photoURL);
      if (candidate.workStatus) params.set('workStatus', candidate.workStatus);
      if (candidate.description) params.set('description', candidate.description);
      if (candidate.keySkills) params.set('keySkills', candidate.keySkills);
    }
    
    // Add slot ID if viewing specific interview
    if (slotId) {
      params.set('slotId', slotId);
    }
    
    const url = `/AdminCandidateViewProfile?${params.toString()}`;
    window.open(url, '_blank');
  };

  // Format time
  const formatTime = (t) => {
    if (!t) return 'N/A';
    const [h, m] = t.split(':');
    const hr = parseInt(h, 10);
    return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
  };

  // Stat Box Component
  const Stat = ({ label, value, color, hasMore }) => (
    <div className="bg-slate-800/60 border border-slate-700 rounded px-3 py-2 text-center flex-1 min-w-[100px]">
      <div className={`text-lg font-bold ${color}`}>
        {loadingAnalytics ? "-" : `${value}${hasMore ? '+' : ''}`}
      </div>
      <div className="text-[10px] text-slate-400 uppercase">{label}</div>
    </div>
  );

  // Tab Button
  const Tab = ({ id, label }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`px-3 py-1.5 text-xs rounded transition-colors ${
        activeTab === id ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="h-screen bg-slate-900 text-slate-200 flex flex-col overflow-hidden">
      <Header />
      
      <div className="flex-1 p-3 flex flex-col gap-2 overflow-hidden">
        {/* Header Row: Stats + Tabs */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-2 flex-1">
            <Stat label="Candidates" value={analytics.totalCandidates} color="text-blue-400" hasMore={analytics.candidatesHasMore} />
            <Stat label="Interviews" value={analytics.totalInterviews} color="text-purple-400" hasMore={analytics.interviewsHasMore} />
            <Stat label="Evaluated" value={analytics.completedEvaluations} color="text-green-400" hasMore={analytics.evaluationsHasMore} />
            <Stat label="Pending" value={analytics.pending} color="text-yellow-400" />
          </div>
          <div className="flex gap-1">
            <Tab id="dashboard" label="Candidates" />
            <Tab id="slots" label="Slots by Date" />
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`text-xs px-3 py-1.5 rounded flex justify-between items-center ${
            message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            <span>{message.text}</span>
            <button onClick={() => setMessage({ type: "", text: "" })} className="ml-2 hover:opacity-70">x</button>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          
          {/* CANDIDATES TAB */}
          {activeTab === "dashboard" && (
            <div className="h-full flex gap-3">
              {/* Left: Candidate List */}
              <div className="flex-1 flex flex-col bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
                <div className="p-2 border-b border-slate-700 flex gap-2">
                  <input
                    type="text"
                    placeholder="Search by name, email, phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 bg-slate-700 text-white text-xs border border-slate-600 rounded py-1.5 px-2"
                  />
                  <button onClick={() => fetchCandidates()} className="bg-slate-600 hover:bg-slate-500 text-xs px-3 py-1 rounded">
                    Refresh
                  </button>
                </div>
                
                <div className="flex-1 overflow-y-auto">
                  {isLoadingCandidates ? (
                    <div className="text-center text-slate-500 py-8">Loading candidates...</div>
                  ) : filteredCandidates.length === 0 ? (
                    <div className="text-center text-slate-500 py-8">No candidates found</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="bg-slate-700/50 sticky top-0">
                        <tr className="text-slate-400 text-left">
                          <th className="px-2 py-1.5">Name</th>
                          <th className="px-2 py-1.5">Email</th>
                          <th className="px-2 py-1.5">Phone</th>
                          <th className="px-2 py-1.5">Role</th>
                          <th className="px-2 py-1.5">City</th>
                          <th className="px-2 py-1.5 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700/50">
                        {filteredCandidates.map((c) => (
                          <tr 
                            key={c.id} 
                            className="hover:bg-slate-700/30 cursor-pointer transition-colors"
                            onClick={() => openProfilePage(c.email, c)}
                          >
                            <td className="px-2 py-1.5 text-white">{c.fullName || 'N/A'}</td>
                            <td className="px-2 py-1.5 text-slate-300 truncate max-w-[150px]">{c.email}</td>
                            <td className="px-2 py-1.5 text-slate-400">{c.mobileNumber || 'N/A'}</td>
                            <td className="px-2 py-1.5 text-slate-400 truncate max-w-[100px]">{c.roleApplyFor || 'N/A'}</td>
                            <td className="px-2 py-1.5 text-slate-400">{c.currentCity || 'N/A'}</td>
                            <td className="px-2 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => viewCandidateDetails(c)}
                                className="bg-blue-600/80 hover:bg-blue-600 text-white px-2 py-0.5 rounded text-[10px] mr-1"
                              >
                                Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                
                {hasMore && !isLoadingCandidates && (
                  <div className="p-2 border-t border-slate-700 text-center">
                    <button
                      onClick={() => fetchCandidates(true)}
                      disabled={isLoadingMore}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-4 py-1 rounded disabled:opacity-50"
                    >
                      {isLoadingMore ? "Loading..." : `Load More (${allCandidates.length} loaded)`}
                    </button>
                  </div>
                )}
                {isSearching && <div className="p-1 text-center text-[10px] text-blue-400">Searching server...</div>}
              </div>

              {/* Right: Candidate Details */}
              <div className="w-80 bg-slate-800/50 border border-slate-700 rounded-lg flex flex-col overflow-hidden">
                {selectedCandidate ? (
                  <>
                    <div className="p-3 border-b border-slate-700">
                      <div className="flex items-center gap-3">
                        <img
                          src={selectedCandidate.photoURL || '/profile.png'}
                          alt=""
                          className="w-12 h-12 rounded-full object-cover bg-slate-700"
                          onError={(e) => e.target.src = '/profile.png'}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-medium text-sm truncate">{selectedCandidate.fullName || 'N/A'}</div>
                          <div className="text-slate-400 text-xs truncate">{selectedCandidate.email}</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-3 text-xs space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div><span className="text-slate-500">Phone:</span> <span className="text-slate-300">{selectedCandidate.mobileNumber || 'N/A'}</span></div>
                        <div><span className="text-slate-500">City:</span> <span className="text-slate-300">{selectedCandidate.currentCity || 'N/A'}</span></div>
                        <div><span className="text-slate-500">Role:</span> <span className="text-slate-300">{selectedCandidate.roleApplyFor || 'N/A'}</span></div>
                        <div><span className="text-slate-500">Status:</span> <span className="text-slate-300">{selectedCandidate.workStatus || 'N/A'}</span></div>
                        <div><span className="text-slate-500">Salary:</span> <span className="text-slate-300">{selectedCandidate.preferredSalary || 'N/A'}</span></div>
                        <div><span className="text-slate-500">Mode:</span> <span className="text-slate-300">{selectedCandidate.workMode || 'N/A'}</span></div>
                      </div>
                      
                      {selectedCandidate.keySkills && (
                        <div>
                          <div className="text-slate-500 mb-1">Skills:</div>
                          <div className="text-slate-300 text-[10px]">{selectedCandidate.keySkills}</div>
                        </div>
                      )}
                      
                      {selectedCandidate.description && (
                        <div>
                          <div className="text-slate-500 mb-1">About:</div>
                          <div className="text-slate-300 text-[10px] line-clamp-3">{selectedCandidate.description}</div>
                        </div>
                      )}
                      
                      {/* Resume */}
                      {selectedCandidate.resumeURL && (
                        <div className="pt-2 border-t border-slate-700">
                          <div className="text-slate-500 mb-1">Resume:</div>
                          <a 
                            href={selectedCandidate.resumeURL} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="text-blue-400 text-[10px] hover:underline flex items-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            View Resume
                          </a>
                        </div>
                      )}
                      
                      {/* Evaluations */}
                      <div className="pt-2 border-t border-slate-700">
                        <div className="text-slate-400 font-medium mb-2">Evaluations ({candidateEvaluations.length})</div>
                        {candidateEvaluations.length === 0 ? (
                          <div className="text-slate-500 text-[10px]">No evaluations found</div>
                        ) : (
                          <div className="space-y-1.5">
                            {candidateEvaluations.slice(0, 5).map((ev) => (
                              <div key={ev.id} className="bg-slate-700/50 rounded p-2">
                                <div className="flex justify-between text-[10px]">
                                  <span className="text-slate-400">Round {ev.roundNumber || 'N/A'}</span>
                                  <span className="text-slate-500">{new Date(ev.createdAt).toLocaleDateString()}</span>
                                </div>
                                {ev.videoURL && (
                                  <a href={ev.videoURL} target="_blank" rel="noreferrer" className="text-blue-400 text-[10px] hover:underline">
                                    View Video
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="p-2 border-t border-slate-700 flex gap-2">
                      <button
                        onClick={() => openProfilePage(selectedCandidate.email)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs py-1.5 rounded"
                      >
                        Full Profile
                      </button>
                      <button
                        onClick={() => setSelectedCandidate(null)}
                        className="bg-slate-600 hover:bg-slate-500 text-white text-xs px-3 py-1.5 rounded"
                      >
                        Close
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">
                    Select a candidate
                  </div>
                )}
              </div>
            </div>
          )}

          {/* SLOTS BY DATE TAB */}
          {activeTab === "slots" && (
            <div className="h-full flex flex-col bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
              <div className="p-2 border-b border-slate-700 space-y-2">
                {/* Single Date Option */}
                <div className="flex gap-2 items-center flex-wrap">
                  <span className="text-slate-300 text-xs font-medium">Single Date:</span>
                  <input
                    type="date"
                    onChange={(e) => {
                      const date = e.target.value;
                      setStartDate(date);
                      setEndDate(date);
                    }}
                    className="bg-slate-700 text-white text-xs border border-slate-600 rounded py-1.5 px-2"
                  />
                  <button
                    onClick={() => {
                      const today = new Date().toISOString().split('T')[0];
                      setStartDate(today);
                      setEndDate(today);
                    }}
                    className="bg-purple-600 hover:bg-purple-700 text-white text-xs px-3 py-1.5 rounded"
                  >
                    Today
                  </button>
                </div>
                
                {/* Date Range Option */}
                <div className="flex gap-2 items-center flex-wrap border-t border-slate-700/50 pt-2">
                  <span className="text-slate-300 text-xs font-medium">Date Range:</span>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-400 text-xs">From:</span>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="bg-slate-700 text-white text-xs border border-slate-600 rounded py-1.5 px-2"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-400 text-xs">To:</span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate}
                      className="bg-slate-700 text-white text-xs border border-slate-600 rounded py-1.5 px-2"
                    />
                  </div>
                  <button
                    onClick={fetchSlotsByDate}
                    disabled={loading || !startDate}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white text-xs px-4 py-1.5 rounded"
                  >
                    {loading ? "..." : "Search"}
                  </button>
                  <button
                    onClick={() => { setStartDate(""); setEndDate(""); setInterviewSlots([]); }}
                    className="bg-slate-600 hover:bg-slate-500 text-white text-xs px-3 py-1.5 rounded"
                  >
                    Clear
                  </button>
                  <span className="text-slate-400 text-xs ml-2">
                    {interviewSlots.length > 0 && `Found ${interviewSlots.length} slot(s)`}
                  </span>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="text-center text-slate-500 py-8">Loading...</div>
                ) : interviewSlots.length === 0 ? (
                  <div className="text-center text-slate-500 py-8">
                    {startDate ? "No slots for this date range" : "Select a date to search"}
                  </div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-slate-700/50 sticky top-0">
                      <tr className="text-slate-400 text-left">
                        <th className="px-3 py-2">Email</th>
                        <th className="px-3 py-2">Time</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Meeting</th>
                        <th className="px-3 py-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50">
                      {interviewSlots.map((s) => (
                        <tr key={s.id} className="hover:bg-slate-700/30">
                          <td className="px-3 py-2 text-slate-300">{s.candidateEmail}</td>
                          <td className="px-3 py-2 text-slate-400">{formatTime(s.startTime)} - {formatTime(s.endTime)}</td>
                          <td className="px-3 py-2">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                              s.status?.toLowerCase() === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {s.status || 'pending'}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            {s.meetingLinkURL ? (
                              <a href={s.meetingLinkURL} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">
                                Join
                              </a>
                            ) : 'N/A'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button
                            onClick={() => openProfilePage(s.candidateEmail, null, s.id)}
                            className="bg-green-600/80 hover:bg-green-600 text-white px-2 py-0.5 rounded text-[10px] mr-1"
                            >
                              Profile
                            </button>
                            <button
                              onClick={() => deleteSlot(s.id)}
                              className="bg-red-600/80 hover:bg-red-600 text-white px-2 py-0.5 rounded text-[10px]"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAddContext;
